import { auth } from '../firebase';

// Empty base = same origin. Dev uses the Vite proxy; prod uses Caddy. Override with
// VITE_API_BASE_URL only when the API is on a different origin.
export const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '';

async function authHeader(): Promise<Record<string, string>> {
  const token = await auth.currentUser?.getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Error thrown by the API helpers; carries the HTTP status and parsed JSON body (if any). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    path: string,
  ) {
    super(`API ${path} failed: ${status}`);
    this.name = 'ApiError';
  }
}

async function fail(res: Response, path: string): Promise<never> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    /* no JSON body */
  }
  throw new ApiError(res.status, body, path);
}

export async function apiGet<T>(path: string): Promise<T> {
  // `no-store`: API data is dynamic (bids, prices, status) — never serve a stale browser-cached GET.
  const res = await fetch(`${API_BASE}${path}`, { headers: { ...(await authHeader()) }, cache: 'no-store' });
  if (!res.ok) await fail(res, path);
  return res.json() as Promise<T>;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) await fail(res, path);
  return (res.status === 204 ? undefined : await res.json()) as T;
}

export const apiPost = <T>(path: string, body?: unknown) => send<T>('POST', path, body);
export const apiPatch = <T>(path: string, body?: unknown) => send<T>('PATCH', path, body);
export const apiPut = <T>(path: string, body?: unknown) => send<T>('PUT', path, body);
export const apiDelete = <T>(path: string) => send<T>('DELETE', path);

export interface StreamHandlers {
  onMeta?: (data: { conversationId: string; title: string }) => void;
  onDelta?: (text: string) => void;
  onError?: (message: string) => void;
  signal?: AbortSignal;
}

/**
 * POST that consumes a Server-Sent Events response (used by the AI chat). Parses `event:`/`data:`
 * frames and dispatches `meta` / `delta` / `error`. Resolves when the stream closes.
 */
export async function apiStream(path: string, body: unknown, h: StreamHandlers): Promise<void> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await authHeader()) },
    body: JSON.stringify(body),
    signal: h.signal,
  });
  if (!res.ok) await fail(res, path);
  if (!res.body) throw new ApiError(res.status, null, path);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) !== -1) {
      const frame = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      let event = 'message';
      let data = '';
      for (const line of frame.split('\n')) {
        if (line.startsWith('event:')) event = line.slice(6).trim();
        else if (line.startsWith('data:')) data += line.slice(5).trim();
      }
      if (!data) continue;
      const parsed = JSON.parse(data);
      if (event === 'meta') h.onMeta?.(parsed);
      else if (event === 'delta') h.onDelta?.(parsed.text);
      else if (event === 'error') h.onError?.(parsed.message);
    }
  }
}

/** Public logo URL (cache-busted by the last update time). */
export function logoUrl(version: string | null): string {
  return `${API_BASE}/api/config/logo${version ? `?v=${encodeURIComponent(version)}` : ''}`;
}

/** Resolve a relative API asset path (e.g. a media image URL) against the API base. */
export function assetUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  return /^https?:\/\//.test(path) ? path : `${API_BASE}${path}`;
}
